/**
 * CaseTrackUS - Immigration Case Tracking Platform
 * -------------------------------------------------
 * Module: src/pages/Dashboard.js
 * Purpose: Main dashboard — shows USCIS and EOIR cases in separate sections.
 *          EOIR cases are checked via popup window in client's browser.
 *          Weekly automatic checks via Chrome extension on admin Mac.
 *
 * Author: Irina Panferova
 * Company: Panferov LLC
 * Website: https://casetrackus.com
 * Created: 2025
 * Updated: 2026
 * Copyright: (c) 2026 Panferov LLC. All rights reserved.
 */
import React, { useState, useEffect } from 'react';
import {
Container, Box, Typography, Button, Card, CardContent,
TextField, Dialog, DialogTitle, DialogContent, DialogActions,
IconButton, Chip, Alert, CircularProgress, AppBar, Toolbar,
MenuItem, Select, FormControl, InputLabel, Avatar, Divider, Autocomplete, Menu
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from '@mui/icons-material/History';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import api, {
getCases, createCase, checkCase, deleteCase, getCaseHistory,
getFamilyMembers, createFamilyMember, deleteFamilyMember
} from '../services/api';

// ─────────────────────────────────────
// Constants
// ─────────────────────────────────────
const RELATIONSHIP_TYPES = [
  { value: 'Self', label: '👤 Self (Myself)' },
  { value: 'Spouse', label: '💑 Spouse' },
  { value: 'Child', label: '👧 Child' },
  { value: 'Parent', label: '👨‍👩‍👧 Parent' },
  { value: 'Sibling', label: '👫 Sibling' },
  { value: 'Other', label: '👥 Other' },
];

// EOIR nationality codes — exact copy from acis.eoir.justice.gov dropdown
// These codes MUST match exactly what EOIR has on file
const NATIONALITY_OPTIONS = [
  { code: 'AF', label: 'Afghanistan' },
  { code: 'AL', label: 'Albania' },
  { code: 'AG', label: 'Algeria' },
  { code: 'AN', label: 'Andorra' },
  { code: 'AO', label: 'Angola' },
  { code: 'AV', label: 'Anguilla' },
  { code: 'AC', label: 'Antigua and Barbuda' },
  { code: 'AR', label: 'Argentina' },
  { code: 'AM', label: 'Armenia' },
  { code: 'AB', label: 'Aruba' },
  { code: 'AS', label: 'Australia' },
  { code: 'AU', label: 'Austria' },
  { code: 'AZ', label: 'Azerbaijan' },
  { code: 'BF', label: 'Bahamas' },
  { code: 'BA', label: 'Bahrain' },
  { code: 'BG', label: 'Bangladesh' },
  { code: 'BB', label: 'Barbados' },
  { code: 'XX', label: 'Be Removed From The United States' },
  { code: 'BZ', label: 'Belarus' },
  { code: 'BE', label: 'Belgium' },
  { code: 'BH', label: 'Belize' },
  { code: 'BN', label: 'Benin' },
  { code: 'BD', label: 'Bermuda' },
  { code: 'BT', label: 'Bhutan' },
  { code: 'BL', label: 'Bolivia' },
  { code: 'BI', label: 'Bosnia-Herzegovina' },
  { code: 'BC', label: 'Botswana' },
  { code: 'BV', label: 'Bouvet Island' },
  { code: 'BR', label: 'Brazil' },
  { code: 'IO', label: 'British Indian Ocean Territory' },
  { code: 'VI', label: 'British Virgin Islands' },
  { code: 'BW', label: 'British West Indies' },
  { code: 'BX', label: 'Brunei' },
  { code: 'BU', label: 'Bulgaria' },
  { code: 'BO', label: 'Burkina Faso' },
  { code: 'BM', label: 'Burma (Myanmar)' },
  { code: 'BY', label: 'Burundi' },
  { code: 'BS', label: 'Byelorussia (Belarus)' },
  { code: 'CC', label: 'Cambodia' },
  { code: 'CM', label: 'Cameroon' },
  { code: 'CA', label: 'Canada' },
  { code: 'CV', label: 'Cape Verde' },
  { code: 'CJ', label: 'Cayman Islands' },
  { code: 'CT', label: 'Central African Republic' },
  { code: 'CD', label: 'Chad' },
  { code: 'CI', label: 'Chile' },
  { code: 'CH', label: 'China' },
  { code: 'KT', label: 'Christmas Islands' },
  { code: 'CK', label: 'Cocos Island' },
  { code: 'CO', label: 'Colombia' },
  { code: 'CN', label: 'Comoro Islands' },
  { code: 'CF', label: 'Congo' },
  { code: 'CW', label: 'Cook Islands' },
  { code: 'CS', label: 'Costa Rica' },
  { code: 'CX', label: 'Croatia' },
  { code: 'CU', label: 'Cuba' },
  { code: 'CY', label: 'Cyprus' },
  { code: 'CR', label: 'Czech Republic' },
  { code: 'DC', label: 'Democratic Republic of Congo' },
  { code: 'DA', label: 'Denmark' },
  { code: 'DJ', label: 'Djibouti' },
  { code: 'DO', label: 'Dominica' },
  { code: 'DR', label: 'Dominican Republic' },
  { code: 'TM', label: 'East Timor' },
  { code: 'EC', label: 'Ecuador' },
  { code: 'EG', label: 'Egypt' },
  { code: 'ES', label: 'El Salvador' },
  { code: 'EK', label: 'Equatorial Guinea' },
  { code: 'ER', label: 'Eritrea' },
  { code: 'EO', label: 'Estonia' },
  { code: 'ET', label: 'Ethiopia' },
  { code: 'FO', label: 'Faeroe Island' },
  { code: 'FA', label: 'Falkland Islands' },
  { code: 'FM', label: 'Federated States of Micronesia' },
  { code: 'FJ', label: 'Fiji' },
  { code: 'FI', label: 'Finland' },
  { code: 'CZ', label: 'Former Countries (CZ)' },
  { code: 'GC', label: 'Former Countries (GC)' },
  { code: 'UR', label: 'Former Countries (UR)' },
  { code: 'YO', label: 'Former Countries (YO)' },
  { code: 'YS', label: 'Former Countries (YS)' },
  { code: 'FR', label: 'France' },
  { code: 'FG', label: 'French Guiana' },
  { code: 'FP', label: 'French Polynesia' },
  { code: 'FS', label: 'French Southern and Antarctic Lands' },
  { code: 'FW', label: 'French West Indies' },
  { code: 'GB', label: 'Gabon' },
  { code: 'GA', label: 'Gambia' },
  { code: 'GZ', label: 'Gaza Strip' },
  { code: 'GO', label: 'Georgia' },
  { code: 'GE', label: 'Germany' },
  { code: 'GH', label: 'Ghana' },
  { code: 'GI', label: 'Gibraltar' },
  { code: 'GR', label: 'Greece' },
  { code: 'GL', label: 'Greenland' },
  { code: 'GJ', label: 'Grenada' },
  { code: 'GP', label: 'Guadeloupe' },
  { code: 'GT', label: 'Guatemala' },
  { code: 'GV', label: 'Guinea' },
  { code: 'PU', label: 'Guinea Bissau' },
  { code: 'GY', label: 'Guyana' },
  { code: 'HA', label: 'Haiti' },
  { code: 'HM', label: 'Heard and McDonald Islands' },
  { code: 'HL', label: 'Holland' },
  { code: 'HO', label: 'Honduras' },
  { code: 'HK', label: 'Hong Kong' },
  { code: 'HU', label: 'Hungary' },
  { code: 'IC', label: 'Iceland' },
  { code: 'IU', label: 'In Absentia/Unknown' },
  { code: 'IN', label: 'India' },
  { code: 'ID', label: 'Indonesia' },
  { code: 'IR', label: 'Iran' },
  { code: 'IZ', label: 'Iraq' },
  { code: 'EI', label: 'Ireland' },
  { code: 'IS', label: 'Israel' },
  { code: 'IT', label: 'Italy' },
  { code: 'IV', label: "Ivory Coast (Cote D'Ivoire)" },
  { code: 'JM', label: 'Jamaica' },
  { code: 'JA', label: 'Japan' },
  { code: 'JO', label: 'Jordan' },
  { code: 'CB', label: 'Kampuchea' },
  { code: 'KZ', label: 'Kazakhstan' },
  { code: 'KE', label: 'Kenya' },
  { code: 'KG', label: 'Kirghizia (Kyrgyzstan)' },
  { code: 'KR', label: 'Kiribati' },
  { code: 'KV', label: 'Kosovo' },
  { code: 'KU', label: 'Kuwait' },
  { code: 'LA', label: 'Laos' },
  { code: 'LV', label: 'Latvia' },
  { code: 'LE', label: 'Lebanon' },
  { code: 'LT', label: 'Lesotho' },
  { code: 'LI', label: 'Liberia' },
  { code: 'LY', label: 'Libya' },
  { code: 'LS', label: 'Liechtenstein' },
  { code: 'LH', label: 'Lithuania' },
  { code: 'LU', label: 'Luxembourg' },
  { code: 'MC', label: 'Macau' },
  { code: 'MM', label: 'Macedonia' },
  { code: 'MA', label: 'Madagascar' },
  { code: 'MI', label: 'Malawi' },
  { code: 'MY', label: 'Malaysia' },
  { code: 'MV', label: 'Maldives' },
  { code: 'ML', label: 'Mali' },
  { code: 'MT', label: 'Malta' },
  { code: 'MB', label: 'Martinique' },
  { code: 'MR', label: 'Mauritania' },
  { code: 'MP', label: 'Mauritius' },
  { code: 'MX', label: 'Mexico' },
  { code: 'MQ', label: 'Midway Islands' },
  { code: 'MD', label: 'Moldavia (Moldova)' },
  { code: 'MN', label: 'Monaco' },
  { code: 'MG', label: 'Mongolia' },
  { code: 'MJ', label: 'Montenegro' },
  { code: 'MH', label: 'Montserrat' },
  { code: 'MO', label: 'Morocco' },
  { code: 'MZ', label: 'Mozambique' },
  { code: 'WA', label: 'Namibia' },
  { code: 'NR', label: 'Nauru' },
  { code: 'NP', label: 'Nepal' },
  { code: 'NL', label: 'Netherlands' },
  { code: 'NA', label: 'Netherlands Antilles' },
  { code: 'NC', label: 'New Caledonia' },
  { code: 'NZ', label: 'New Zealand' },
  { code: 'NU', label: 'Nicaragua' },
  { code: 'NG', label: 'Niger' },
  { code: 'NI', label: 'Nigeria' },
  { code: 'NE', label: 'Niue' },
  { code: 'NN', label: 'No Nationality' },
  { code: 'NF', label: 'Norfolk Island' },
  { code: 'KN', label: 'North Korea' },
  { code: 'NO', label: 'Norway' },
  { code: 'MU', label: 'Oman' },
  { code: 'PK', label: 'Pakistan' },
  { code: 'PN', label: 'Palestine' },
  { code: 'PM', label: 'Panama' },
  { code: 'PP', label: 'Papua New Guinea' },
  { code: 'PA', label: 'Paraguay' },
  { code: 'DM', label: "People's Republic of Benin" },
  { code: 'PE', label: 'Peru' },
  { code: 'RP', label: 'Philippines' },
  { code: 'PC', label: 'Pitcairn Islands' },
  { code: 'PL', label: 'Poland' },
  { code: 'PO', label: 'Portugal' },
  { code: 'QA', label: 'Qatar' },
  { code: 'RE', label: 'Reunioun' },
  { code: 'RO', label: 'Romania' },
  { code: 'RU', label: 'Russia' },
  { code: 'RW', label: 'Rwanda' },
  { code: 'WS', label: 'Samoa' },
  { code: 'SM', label: 'San Marino' },
  { code: 'TP', label: 'Sao Tome and Principe' },
  { code: 'SA', label: 'Saudi Arabia' },
  { code: 'SG', label: 'Senegal' },
  { code: 'RS', label: 'Serbia' },
  { code: 'SE', label: 'Seychelles' },
  { code: 'SL', label: 'Sierra Leone' },
  { code: 'SN', label: 'Singapore' },
  { code: 'SR', label: 'Slovak Republic' },
  { code: 'SV', label: 'Slovenia' },
  { code: 'BP', label: 'Solomon Islands' },
  { code: 'SO', label: 'Somalia' },
  { code: 'SF', label: 'South Africa' },
  { code: 'KS', label: 'South Korea' },
  { code: 'XS', label: 'South Sudan' },
  { code: 'SP', label: 'Spain' },
  { code: 'CE', label: 'Sri Lanka' },
  { code: 'SC', label: 'St. Christopher-Nevis' },
  { code: 'SH', label: 'St. Helena' },
  { code: 'SK', label: 'St. Kitts, West Indies' },
  { code: 'ST', label: 'St. Lucia' },
  { code: 'SB', label: 'St. Pierre and Miquelon' },
  { code: 'VC', label: 'St. Vincent and the Grenadines' },
  { code: 'SS', label: 'Stateless - Alien Unable to Name a Country' },
  { code: 'SU', label: 'Sudan' },
  { code: 'NS', label: 'Suriname' },
  { code: 'WZ', label: 'Swaziland' },
  { code: 'SW', label: 'Sweden' },
  { code: 'SZ', label: 'Switzerland' },
  { code: 'SY', label: 'Syria' },
  { code: 'TW', label: 'Taiwan' },
  { code: 'TA', label: 'Tajikistan (Tadzhik)' },
  { code: 'TZ', label: 'Tanzania' },
  { code: 'TH', label: 'Thailand' },
  { code: 'PS', label: 'The Republic of Palau' },
  { code: 'RM', label: 'The Republic of the Marshall Islands' },
  { code: 'TO', label: 'Togo' },
  { code: 'TL', label: 'Tokelau' },
  { code: 'TN', label: 'Tonga' },
  { code: 'TD', label: 'Trinidad and Tobago' },
  { code: 'TS', label: 'Tunisia' },
  { code: 'TU', label: 'Turkiye' },
  { code: 'TR', label: 'Turkmenistan' },
  { code: 'TK', label: 'Turks and Caicos Islands' },
  { code: 'TV', label: 'Tuvalu' },
  { code: 'UG', label: 'Uganda' },
  { code: 'UE', label: 'Ukraine' },
  { code: 'TC', label: 'United Arab Emirates' },
  { code: 'UK', label: 'United Kingdom' },
  { code: 'UN', label: 'Unknown Nationality' },
  { code: 'UV', label: 'Upper Volta' },
  { code: 'UY', label: 'Uruguay' },
  { code: 'UZ', label: 'Uzbekistan' },
  { code: 'NH', label: 'Vanuatu' },
  { code: 'VE', label: 'Venezuela' },
  { code: 'VM', label: 'Vietnam' },
  { code: 'WI', label: 'Western Sahara' },
  { code: 'YE', label: 'Yemen' },
  { code: 'CG', label: 'Zaire' },
  { code: 'ZA', label: 'Zambia' },
  { code: 'ZI', label: 'Zimbabwe' },
];

// NAT_NAMES used in popup script to match EOIR dropdown labels
// These are the UPPERCASE names as they appear in EOIR dropdown
const NAT_NAMES = {
  'AF': 'AFGHANISTAN', 'AL': 'ALBANIA', 'AG': 'ALGERIA',
  'AM': 'ARMENIA', 'AZ': 'AZERBAIJAN', 'BG': 'BANGLADESH',
  'XX': 'BE REMOVED FROM THE UNITED STATES', 'BZ': 'BELARUS',
  'BE': 'BELGIUM', 'BH': 'BELIZE', 'BL': 'BOLIVIA',
  'BI': 'BOSNIA-HERZEGOVINA', 'BR': 'BRAZIL', 'BU': 'BULGARIA',
  'BO': 'BURKINA FASO', 'BM': 'BURMA (MYANMAR)', 'BY': 'BURUNDI',
  'BS': 'BYELORUSSIA (BELARUS)', 'CC': 'CAMBODIA', 'CM': 'CAMEROON',
  'CA': 'CANADA', 'CT': 'CENTRAL AFRICAN REPUBLIC', 'CD': 'CHAD',
  'CI': 'CHILE', 'CH': 'CHINA', 'CO': 'COLOMBIA', 'CF': 'CONGO',
  'CS': 'COSTA RICA', 'CX': 'CROATIA', 'CU': 'CUBA',
  'CR': 'CZECH REPUBLIC', 'DC': 'DEMOCRATIC REPUBLIC OF CONGO',
  'DA': 'DENMARK', 'DO': 'DOMINICA', 'DR': 'DOMINICAN REPUBLIC',
  'EC': 'ECUADOR', 'EG': 'EGYPT', 'ES': 'EL SALVADOR',
  'ER': 'ERITREA', 'ET': 'ETHIOPIA', 'FI': 'FINLAND',
  'FR': 'FRANCE', 'GB': 'GABON', 'GA': 'GAMBIA', 'GO': 'GEORGIA',
  'GE': 'GERMANY', 'GH': 'GHANA', 'GR': 'GREECE', 'GT': 'GUATEMALA',
  'GV': 'GUINEA', 'GY': 'GUYANA', 'HA': 'HAITI', 'HO': 'HONDURAS',
  'HK': 'HONG KONG', 'HU': 'HUNGARY', 'IN': 'INDIA',
  'ID': 'INDONESIA', 'IR': 'IRAN', 'IZ': 'IRAQ', 'EI': 'IRELAND',
  'IS': 'ISRAEL', 'IT': 'ITALY', 'JM': 'JAMAICA', 'JA': 'JAPAN',
  'JO': 'JORDAN', 'KZ': 'KAZAKHSTAN', 'KE': 'KENYA',
  'KG': 'KIRGHIZIA (KYRGYZSTAN)', 'KV': 'KOSOVO', 'KU': 'KUWAIT',
  'LA': 'LAOS', 'LV': 'LATVIA', 'LE': 'LEBANON', 'LI': 'LIBERIA',
  'LY': 'LIBYA', 'LH': 'LITHUANIA', 'MA': 'MADAGASCAR',
  'MY': 'MALAYSIA', 'ML': 'MALI', 'MR': 'MAURITANIA',
  'MX': 'MEXICO', 'MD': 'MOLDAVIA (MOLDOVA)', 'MG': 'MONGOLIA',
  'MO': 'MOROCCO', 'MZ': 'MOZAMBIQUE', 'NP': 'NEPAL',
  'NL': 'NETHERLANDS', 'NZ': 'NEW ZEALAND', 'NU': 'NICARAGUA',
  'NG': 'NIGER', 'NI': 'NIGERIA', 'NN': 'NO NATIONALITY',
  'KN': 'NORTH KOREA', 'NO': 'NORWAY', 'PK': 'PAKISTAN',
  'PN': 'PALESTINE', 'PM': 'PANAMA', 'PA': 'PARAGUAY',
  'PE': 'PERU', 'RP': 'PHILIPPINES', 'PL': 'POLAND',
  'PO': 'PORTUGAL', 'QA': 'QATAR', 'RO': 'ROMANIA',
  'RU': 'RUSSIA', 'RW': 'RWANDA', 'SA': 'SAUDI ARABIA',
  'SG': 'SENEGAL', 'RS': 'SERBIA', 'SL': 'SIERRA LEONE',
  'SO': 'SOMALIA', 'SF': 'SOUTH AFRICA', 'KS': 'SOUTH KOREA',
  'XS': 'SOUTH SUDAN', 'SP': 'SPAIN', 'CE': 'SRI LANKA',
  'SS': 'STATELESS - ALIEN UNABLE TO NAME A COUNTRY',
  'SU': 'SUDAN', 'SW': 'SWEDEN', 'SZ': 'SWITZERLAND',
  'SY': 'SYRIA', 'TW': 'TAIWAN', 'TA': 'TAJIKISTAN (TADZHIK)',
  'TZ': 'TANZANIA', 'TH': 'THAILAND', 'TO': 'TOGO',
  'TD': 'TRINIDAD AND TOBAGO', 'TS': 'TUNISIA', 'TU': 'TURKIYE',
  'TR': 'TURKMENISTAN', 'UG': 'UGANDA', 'UE': 'UKRAINE',
  'TC': 'UNITED ARAB EMIRATES', 'UK': 'UNITED KINGDOM',
  'UN': 'UNKNOWN NATIONALITY', 'UY': 'URUGUAY', 'UZ': 'UZBEKISTAN',
  'VE': 'VENEZUELA', 'VM': 'VIETNAM', 'YE': 'YEMEN',
  'ZA': 'ZAMBIA', 'ZI': 'ZIMBABWE',
};

// ─────────────────────────────────────
// Dashboard Component
// ─────────────────────────────────────
export default function Dashboard({ user, onLogout }) {
  // ─────────────────────────────────────
  // State
  // ─────────────────────────────────────
  const [cases, setCases] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [loading, setLoading] = useState(true);
  // Dialogs
  const [addCaseOpen, setAddCaseOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  // Add case form — shared fields
  const [newCaseType, setNewCaseType] = useState('uscis');
  const [newCaseMemberId, setNewCaseMemberId] = useState('');
  const [newDesc, setNewDesc] = useState('');
  // USCIS form fields
  const [newReceipt, setNewReceipt] = useState('');
  const [newDolNumber, setNewDolNumber] = useState('');
  // EOIR form fields
  const [newAlienNumber, setNewAlienNumber] = useState('');
  const [newNationalityCode, setNewNationalityCode] = useState('');
  // Add family member form
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberRelation, setNewMemberRelation] = useState('Self');
  const [newMemberAlien, setNewMemberAlien] = useState('');
  const [newMemberDob, setNewMemberDob] = useState('');
  const [newMemberNotes, setNewMemberNotes] = useState('');
  const [error, setError] = useState('');
  const [checkingId, setCheckingId] = useState(null);
  // EOIR popup status message
  const [eoirMsg, setEoirMsg] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsName, setSettingsName] = useState(user?.full_name || '');
  const [settingsEmail, setSettingsEmail] = useState(user?.email || '');
  const [settingsEmailNotif, setSettingsEmailNotif] = useState(user?.email_notifications !== false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ─────────────────────────────────────
  // Poll every 30s while there are pending EOIR cases
  // ─────────────────────────────────────
  useEffect(() => {
    const hasPending = cases.some(c => c.case_type === 'eoir' && !c.latest_eoir_check);
    if (!hasPending) return;
    const interval = setInterval(() => loadAll(), 60000);
    return () => clearInterval(interval);
  }, [cases]);

  // ─────────────────────────────────────
  // Load Data
  // ─────────────────────────────────────
  useEffect(() => {
    loadAll(); // eslint-disable-line react-hooks/exhaustive-deps
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    try {
      setLoading(true);
      const [casesRes, membersRes] = await Promise.all([
        getCases(),
        getFamilyMembers()
      ]);
      const casesWithHistory = await Promise.all(
        casesRes.data.map(async (c) => {
          try {
            const hist = await getCaseHistory(c.id);
            return { ...c, checks: hist.data.checks || [] };
          } catch {
            return { ...c, checks: [] };
          }
        })
      );
      setCases(casesWithHistory);
      setFamilyMembers(membersRes.data);
      if (membersRes.data.length > 0 && !selectedMember) {
        setSelectedMember('all');
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────
  // EOIR Check via Popup Window
  // ─────────────────────────────────────
  // Opens EOIR in client's browser — their IP handles captcha automatically.
  // No need for Chrome extension for manual/on-demand checks.
  // Chrome extension handles only weekly automatic Saturday checks.
  const checkEOIRViaPopup = (caseId, alienNumber, nationalityCode) => {
    return new Promise((resolve) => {
      const digits = (alienNumber || '').replace(/\D/g, '');
      const natName = NAT_NAMES[nationalityCode] || nationalityCode || '';

      const popup = window.open(
        'https://acis.eoir.justice.gov/en/',
        'eoir_check_' + caseId,
        'width=900,height=700,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        setError('Please allow popups for casetrackus.com to check EOIR status.');
        resolve();
        return;
      }

      setEoirMsg('Checking EOIR status... A browser window has opened automatically.');
      let done = false;

      const finish = () => {
        if (!done) {
          done = true;
          setEoirMsg('');
          resolve();
        }
      };

      const checkPopup = () => {
        if (done) return;
        if (popup.closed) { finish(); return; }

        try {
          const url = popup.location.href;
          if (!url.includes('acis.eoir')) return;

          // Result page — extract data from localStorage
          if (url.includes('caseInformation')) {
            try {
              const raw = popup.localStorage.getItem('caseInformation');
              if (raw) {
                const data = JSON.parse(raw);
                fetch('https://casetrackus.com/eoir-webhook', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ alien_number: digits, case_id: caseId, data })
                }).then(() => {
                  setTimeout(() => {
                    try { popup.close(); } catch(e) {}
                    loadAll();
                    finish();
                  }, 1500);
                }).catch(finish);
              }
            } catch(e) {}
            return;
          }

        } catch(e) {
          // Cross-origin navigation in progress — keep polling
        }
      };

      const interval = setInterval(checkPopup, 500);

      // Timeout after 3 minutes
      setTimeout(() => {
        clearInterval(interval);
        try { if (!popup.closed) popup.close(); } catch(e) {}
        finish();
      }, 180000);
    });
  };

  // ─────────────────────────────────────
  // Family Member Handlers
  // ─────────────────────────────────────
  const handleAddMember = async () => {
    if (!newMemberName.trim()) return;
    try {
      await createFamilyMember({
        full_name: newMemberName,
        relationship_type: newMemberRelation,
        alien_number: newMemberAlien || null,
        date_of_birth: newMemberDob || null,
        notes: newMemberNotes || null,
      });
      setAddMemberOpen(false);
      setNewMemberName(''); setNewMemberRelation('Self');
      setNewMemberAlien(''); setNewMemberDob(''); setNewMemberNotes('');
      loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add family member');
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Delete this family member and all their cases?')) return;
    try {
      await deleteFamilyMember(memberId);
      if (selectedMember === memberId) setSelectedMember('all');
      loadAll();
    } catch (err) {
      setError('Failed to delete family member');
    }
  };

  // ─────────────────────────────────────
  // Case Handlers
  // ─────────────────────────────────────
  const resetCaseForm = () => {
    setNewCaseType('uscis');
    setNewReceipt('');
    setNewAlienNumber('');
    setNewNationalityCode('');
    setNewDolNumber('');
    setNewDesc('');
    setNewCaseMemberId('');
  };

  const handleAddCase = async () => {
    try {
      if (newCaseType === 'uscis') {
        await createCase({
          case_type: 'uscis',
          receipt_number: newReceipt.toUpperCase(),
          description: newDesc || null,
          family_member_id: newCaseMemberId ? parseInt(newCaseMemberId) : null,
        });
        setAddCaseOpen(false);
        resetCaseForm();
        loadAll();
      } else if (newCaseType === 'dol') {
        await createCase({
          case_type: 'dol',
          dol_case_number: newDolNumber.trim(),
          description: newDesc || null,
          family_member_id: newCaseMemberId ? parseInt(newCaseMemberId) : null,
        });
        setAddCaseOpen(false);
        resetCaseForm();
        loadAll();
      } else {
        // EOIR case
        const natOption = NATIONALITY_OPTIONS.find(n => n.code === newNationalityCode);
        const cleanNumber = newAlienNumber.replace(/\D/g, '');
        const created = await createCase({
          case_type: 'eoir',
          eoir_alien_number: cleanNumber,
          eoir_nationality_code: newNationalityCode,
          eoir_nationality_label: natOption?.label || newNationalityCode,
          description: newDesc || null,
          family_member_id: newCaseMemberId ? parseInt(newCaseMemberId) : null,
        });
        setAddCaseOpen(false);
        resetCaseForm();
        await loadAll();
        // Add to EOIR queue — Chrome extension will pick it up within 30 seconds
        const caseId = created?.data?.id;
        if (caseId) {
          try {
            await api.post(`/eoir-queue/add?case_id=${caseId}`);
            setError('');
            setEoirMsg('⏳ Your case has been added to the queue. Status will be updated shortly. You will receive weekly email updates if anything changes.');
            setTimeout(() => { loadAll(); setEoirMsg(''); }, 120000); // refresh after 2 minutes
          } catch(e) { console.warn('Queue add failed:', e); }
          loadAll();
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add case');
    }
  };

  const handleCheck = async (id, caseType, alienNumber, nationalityCode) => {
    try {
      setCheckingId(id);
      if (caseType === 'eoir') {
        // EOIR: add to queue — Chrome extension picks it up within 30 seconds
        try {
          await api.post(`/eoir-queue/add?case_id=${id}`);
          setError('');
          // Show brief success message
          setTimeout(() => loadAll(), 2000);
        } catch(e) {
          setError('Failed to add to queue. Please try again.');
        }
        loadAll();
      } else {
        // USCIS: direct API call
        await checkCase(id);
        loadAll();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to check case status');
    } finally {
      setCheckingId(null);
    }
  };

  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    setSettingsMsg('');
    try {
      const token = localStorage.getItem('token');
      const res = await api.put(`/auth/me?token=${token}`, {
        full_name: settingsName,
        email_notifications: settingsEmailNotif
      });
      const updatedUser = { ...user, ...res.data };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setSettingsMsg('✅ Settings saved successfully!');
    } catch(err) {
      setSettingsMsg('❌ Failed to save. Please try again.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') return;
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/auth/account?token=${token}&reason=${encodeURIComponent(deleteReason)}`);
      localStorage.clear();
      onLogout();
    } catch(err) {
      setError('Failed to delete account. Please try again.');
      setDeleteLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this case?')) return;
    try {
      await deleteCase(id);
      loadAll();
    } catch (err) {
      setError('Failed to delete case');
    }
  };

  const handleHistory = async (id) => {
    try {
      const res = await getCaseHistory(id);
      setHistoryData(res.data);
      setHistoryOpen(true);
    } catch (err) {
      setError('Failed to load history');
    }
  };

  // ─────────────────────────────────────
  // Helper Functions
  // ─────────────────────────────────────
  const getStatusColor = (status) => {
    if (!status) return 'default';
    const s = status.toLowerCase();
    if (s.includes('approved') || s.includes('granted')) return 'success';
    if (s.includes('denied') || s.includes('rejected')) return 'error';
    if (s.includes('received') || s.includes('scheduled')) return 'info';
    if (s.includes('mailed') || s.includes('sent')) return 'success';
    if (s.includes('bia') || s.includes('appeal') || s.includes('pending')) return 'warning';
    return 'warning';
  };

  const getLatestStatus = (checks) => {
    if (!checks || checks.length === 0) return null;
    return checks[0]?.status || null;
  };

  const getMemberName = (memberId) => {
    if (!memberId) return null;
    const m = familyMembers.find(m => m.id === memberId);
    return m ? m.full_name : null;
  };

  const fmtDate = (d) => {
    if (!d) return null;
    try {
      const date = new Date(d);
      if (isNaN(date)) return d;
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch { return d; }
  };

  // Filter cases by selected member
  const allFiltered = selectedMember === 'all' || !selectedMember
    ? cases
    : cases.filter(c => c.family_member_id === selectedMember);

  // Split into USCIS and EOIR
  const uscisCases = allFiltered.filter(c => c.case_type === 'uscis' || !c.case_type);
  const eoirCases = allFiltered.filter(c => c.case_type === 'eoir');
  const dolCases = allFiltered.filter(c => c.case_type === 'dol');

  const canAddUscis = newCaseType === 'uscis' && newReceipt.length === 13;
  const canAddEoir = newCaseType === 'eoir' && newAlienNumber.replace(/\D/g, '').length === 9 && newNationalityCode;
  const canAddDol = newCaseType === 'dol' && /^[A-Z]-\d{3}-\d{5}-\d{6}$/.test(newDolNumber.trim());

  // ─────────────────────────────────────
  // Sub-components
  // ─────────────────────────────────────

  /** Action buttons for any case card */
  const CaseActions = ({ caseId, caseType, alienNumber, nationalityCode }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 2 }}>
      {caseType !== 'eoir' && (
      <IconButton
        onClick={() => handleCheck(caseId, caseType, alienNumber, nationalityCode)}
        disabled={checkingId === caseId}
        title="Check Status" sx={{ color: '#2E7D32' }}>
        {checkingId === caseId ? <CircularProgress size={20} /> : <RefreshIcon />}
      </IconButton>
      )}
      <IconButton onClick={() => handleHistory(caseId)} title="View History" sx={{ color: '#388E3C' }}>
        <HistoryIcon />
      </IconButton>
      <IconButton onClick={() => handleDelete(caseId)} title="Delete" sx={{ color: '#d32f2f' }}>
        <DeleteIcon />
      </IconButton>
    </Box>
  );

  /** USCIS case card */
  const UscisCard = ({ c }) => {
    const latestStatus = getLatestStatus(c.checks);
    const memberName = getMemberName(c.family_member_id);
    return (
      <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: '4px solid #1565C0' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                <Chip label="USCIS" size="small" sx={{ background: '#E3F2FD', color: '#1565C0', fontWeight: 'bold', fontSize: '0.7rem' }} />
                {memberName && <Chip label={memberName} size="small" sx={{ background: '#E8F5E9', color: '#1B5E20', fontWeight: 'bold' }} />}
              </Box>
              <Typography variant="h6" fontWeight="bold" color="#1565C0">{c.receipt_number}</Typography>
              {c.form_type && (
                <Typography variant="body2" color="text.secondary">
                  Form: {c.form_type}{c.submitted_date && ` • Submitted: ${c.submitted_date}`}
                </Typography>
              )}
              {c.description && c.description !== 'string' && (
                <Typography variant="body2" color="text.secondary">{c.description}</Typography>
              )}
              <Box sx={{ mt: 1 }}>
                {latestStatus ? (
                  <>
                    <Chip label={latestStatus} color={getStatusColor(latestStatus)} size="small" />
                    {c.checks[0]?.checked_at && (() => {
                      const last = new Date(c.checks[0].checked_at);
                      const next = new Date(last);
                      next.setDate(next.getDate() + (6 - next.getDay() + 7) % 7 || 7);
                      return (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ✅ Updated: {last.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {' · '}Next auto-check: {next.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </Typography>
                      );
                    })()}
                  </>
                ) : (
                  <Chip label="Not checked yet" color="default" size="small" />
                )}
              </Box>
              {c.checks[0]?.details && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.75rem' }}>
                  {c.checks[0].details.length > 120 ? c.checks[0].details.substring(0, 120) + '...' : c.checks[0].details}
                </Typography>
              )}
            </Box>
            <CaseActions caseId={c.id} caseType="uscis" />
          </Box>
        </CardContent>
      </Card>
    );
  };

  /** EOIR case card */
  const EoirCard = ({ c }) => {
    const latestStatus = getLatestStatus(c.checks);
    const memberName = getMemberName(c.family_member_id);
    const hasData = !!c.eoir_alien_name; // true after first successful check

    // Format court address: "55 E MONROE|SUITE 1500|CHICAGO, IL 60603" → readable
    const formatAddress = (addr) => {
      if (!addr) return null;
      return addr.split('|').filter(Boolean).join(', ');
    };

    // Decision color based on decision code
    const decisionColor = c.eoir_decision_code === 'R' ? '#2E7D32'
      : c.eoir_decision_code === 'D' ? '#C62828'
      : c.eoir_decision_code === 'V' ? '#1565C0'
      : '#E65100';

    return (
      <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: '4px solid #6A1B9A' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              {/* Header chips */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                <Chip label="EOIR" size="small" sx={{ background: '#F3E5F5', color: '#6A1B9A', fontWeight: 'bold', fontSize: '0.7rem' }} />
                {memberName && <Chip label={memberName} size="small" sx={{ background: '#E8F5E9', color: '#1B5E20', fontWeight: 'bold' }} />}
              </Box>

              {/* A-Number + Name */}
              <Typography variant="h6" fontWeight="bold" color="#6A1B9A">A-{c.eoir_alien_number}</Typography>
              {c.eoir_alien_name && <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{c.eoir_alien_name}</Typography>}
              <Typography variant="body2" color="text.secondary">
                Nationality: {c.eoir_nationality_label || c.eoir_nationality_code}
                {c.eoir_docket_date && ` • Docket Date: ${fmtDate(c.eoir_docket_date)}`}
              </Typography>
              {c.description && c.description !== 'string' && (
                <Typography variant="body2" color="text.secondary">{c.description}</Typography>
              )}

              {/* Data sections — only after first check */}
              {hasData && (
                <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                  {/* Next Hearing */}
                  <Box sx={{ background: '#F8F9FA', borderRadius: 1.5, p: 1.5 }}>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      📅 Next Hearing
                    </Typography>
                    {c.eoir_next_hearing_date ? (
                      <>
                        <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                          {fmtDate(c.eoir_next_hearing_date)}{c.eoir_next_hearing_time && ` at ${c.eoir_next_hearing_time}`}
                        </Typography>
                        {c.eoir_hearing_type && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {c.eoir_hearing_type === 'I' ? 'Individual Hearing' : c.eoir_hearing_type === 'M' ? 'Master Hearing' : c.eoir_hearing_type}
                          </Typography>
                        )}
                        {c.eoir_judge_name && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Judge: {c.eoir_judge_name}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        No future hearings scheduled
                      </Typography>
                    )}
                  </Box>

                  {/* Court Decision */}
                  <Box sx={{ background: '#F8F9FA', borderRadius: 1.5, p: 1.5 }}>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      ⚖️ Court Decision
                    </Typography>
                    {c.eoir_decision ? (
                      <>
                        <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5, color: decisionColor }}>
                          {c.eoir_decision}
                        </Typography>
                        {c.eoir_decision_date && (
                          <Typography variant="caption" color="text.secondary">
                            Decision Date: {fmtDate(c.eoir_decision_date)}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        No decision yet
                      </Typography>
                    )}
                    {c.eoir_court_address && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        📍 {formatAddress(c.eoir_court_address)}
                      </Typography>
                    )}
                  </Box>

                  {/* BIA Appeal */}
                  {(c.eoir_appeal_filed || c.eoir_pending_at_bia) && (
                    <Box sx={{ background: '#FFF8E1', borderRadius: 1.5, p: 1.5 }}>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        📋 BIA Appeal
                      </Typography>

                      {/* 1. Appeal deadline — самое важное, вверху */}
                      {c.eoir_appeal_due_date && (
                        <Box sx={{ mt: 0.5, p: 1, background: '#FFEBEE', borderRadius: 1, border: '1px solid #EF9A9A' }}>
                          <Typography variant="body2" color="error" fontWeight="bold">
                            ⚠️ Appeal deadline: {fmtDate(c.eoir_appeal_due_date)}
                          </Typography>
                        </Box>
                      )}

                      {/* 2. Filed date */}
                      {c.eoir_appeal_filed_date && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Filed: {fmtDate(c.eoir_appeal_filed_date)}
                        </Typography>
                      )}

                      {/* 3. Current status */}
                      {c.eoir_pending_at_bia && (
                        <Chip label="Pending at BIA" color="warning" size="small" sx={{ mt: 0.5 }} />
                      )}

                      {/* 4. BIA decision if any */}
                      {c.eoir_bia_decision && (
                        <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                          BIA Decision: {c.eoir_bia_decision}
                          {c.eoir_bia_decision_date && ` (${fmtDate(c.eoir_bia_decision_date)})`}
                        </Typography>
                      )}

                      {/* 5. Brief dates */}
                      {c.eoir_alien_brief_due && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                          ⚠️ Alien brief due: {fmtDate(c.eoir_alien_brief_due)}
                        </Typography>
                      )}
                      {!c.eoir_alien_brief_due && c.eoir_pending_at_bia && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Alien brief: No due date at this time
                        </Typography>
                      )}
                      {c.eoir_dhs_brief_due && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          DHS brief due: {fmtDate(c.eoir_dhs_brief_due)}
                        </Typography>
                      )}
                      {!c.eoir_dhs_brief_due && c.eoir_pending_at_bia && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          DHS brief: No due date at this time
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Asylum Clock */}
                  {c.eoir_clock_status && (
                    <Box sx={{ background: '#F3F4F6', borderRadius: 1.5, p: 1.5 }}>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        ⏱ Asylum Clock
                      </Typography>
                      <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                        {c.eoir_clock_status === 'X' ? 'Stopped' : c.eoir_clock_status === 'R' ? 'Running' : c.eoir_clock_status}
                      </Typography>
                      {c.eoir_elapsed_days && c.eoir_elapsed_days !== '0000' && (
                        <Typography variant="caption" color="text.secondary">
                          {c.eoir_elapsed_days} days elapsed
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* Last check status */}
              <Box sx={{ mt: 1.5 }}>
                {latestStatus ? (
                  <>
                    <Chip label={latestStatus} color={getStatusColor(latestStatus)} size="small" />
                    {c.checks[0]?.checked_at && (() => {
                      const last = new Date(c.checks[0].checked_at);
                      const next = new Date(last);
                      next.setDate(next.getDate() + (6 - next.getDay() + 7) % 7 || 7);
                      return (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ✅ Updated: {last.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {' · '}Next auto-check: {next.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </Typography>
                      );
                    })()}
                  </>
                ) : (
                  checkingId === c.id
                    ? <Chip icon={<CircularProgress size={12} sx={{ ml: '8px !important' }} />}
                        label="Checking status... this may take a minute"
                        color="warning" size="small" sx={{ pl: 0.5 }} />
                    : c.latest_eoir_check
                      ? <Typography variant="caption" color="text.secondary">
                          ✅ Updated: {new Date(c.latest_eoir_check.checked_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {' · '}Next auto-check: {(() => { const d = new Date(c.latest_eoir_check.checked_at); d.setDate(d.getDate() + (6 - d.getDay() + 7) % 7 || 7); return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); })()}
                        </Typography>
                      : <Alert severity="info" icon={false} sx={{ fontSize: '0.75rem', py: 0.5 }}>
                          ⏳ Case added to queue. Status will be updated shortly. You will receive weekly email updates if anything changes.
                        </Alert>
                )}
              </Box>
            </Box>
            <CaseActions caseId={c.id} caseType="eoir" alienNumber={c.eoir_alien_number} nationalityCode={c.eoir_nationality_code} />
          </Box>
        </CardContent>
      </Card>
    );
  };

  // ─────────────────────────────────────
  // Render
  // ─────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', background: '#f5f7fa' }}>
      {/* AppBar */}
      <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #2E7D32 0%, #66BB6A 100%)' }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            <span style={{ fontSize: '1.8rem', marginRight: '8px' }}>🗽</span>CaseTrackUS
          </Typography>
          <Typography sx={{ mr: 2, opacity: 0.8 }}>{user?.full_name || user?.email}</Typography>
          <IconButton color="inherit" onClick={e => setMenuAnchor(e.currentTarget)}>
            <SettingsIcon />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            <MenuItem onClick={() => { setMenuAnchor(null); setSettingsOpen(true); }}>
              ⚙️ Account Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setMenuAnchor(null); setDeleteOpen(true); }}
              sx={{ color: '#C62828' }}>
              🗑️ Delete Account
            </MenuItem>
          </Menu>
          <IconButton color="inherit" onClick={onLogout}><LogoutIcon /></IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4 }}>
        {/* Error Alert */}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* EOIR checking message — show only while pending */}
        {eoirMsg && cases.some(c => c.case_type === 'eoir' && !c.latest_eoir_check) && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={20} />}>
            {eoirMsg}
          </Alert>
        )}

        {/* Family Members Panel */}
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" color="#1B5E20">
                👨‍👩‍👧 Family Members ({familyMembers.length})
              </Typography>
              <Button variant="outlined" size="small" startIcon={<PersonAddIcon />}
                onClick={() => setAddMemberOpen(true)}
                sx={{ color: '#2E7D32', borderColor: '#2E7D32' }}>
                Add Member
              </Button>
            </Box>
            {familyMembers.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                No family members yet. Add yourself or a family member to start tracking cases.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`All (${cases.length})`}
                  onClick={() => setSelectedMember('all')}
                  color={selectedMember === 'all' ? 'success' : 'default'}
                  variant={selectedMember === 'all' ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }} />
                {familyMembers.map(member => {
                  const memberCases = cases.filter(c => c.family_member_id === member.id);
                  return (
                    <Chip key={member.id}
                      avatar={<Avatar sx={{ bgcolor: '#2E7D32', width: 24, height: 24, fontSize: '0.7rem' }}>
                        {member.full_name.charAt(0)}
                      </Avatar>}
                      label={`${member.full_name} (${memberCases.length})`}
                      onClick={() => setSelectedMember(member.id)}
                      onDelete={() => handleDeleteMember(member.id)}
                      color={selectedMember === member.id ? 'success' : 'default'}
                      variant={selectedMember === member.id ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer' }} />
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Loading */}
        {loading ? (
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <CircularProgress sx={{ color: '#2E7D32' }} />
          </Box>
        ) : (
          <>
            {/* USCIS Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountBalanceIcon sx={{ color: '#1565C0' }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="#1565C0">USCIS — Immigration Application</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {uscisCases.length} case{uscisCases.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </Box>
                <Button variant="outlined" size="small" startIcon={<AddIcon />}
                  onClick={() => { setNewCaseType('uscis'); setAddCaseOpen(true); if (selectedMember && selectedMember !== 'all') setNewCaseMemberId(String(selectedMember)); }}
                  sx={{ color: '#1565C0', borderColor: '#1565C0' }}>
                  Add USCIS case
                </Button>
              </Box>
              {uscisCases.length === 0 ? (
                <Card sx={{ textAlign: 'center', p: 3, borderRadius: 2, border: '1px dashed #BBDEFB', background: '#F8FBFF' }}>
                  <Typography color="text.secondary" variant="body2">No USCIS cases yet. Add a receipt number to start tracking.</Typography>
                </Card>
              ) : uscisCases.map(c => <UscisCard key={c.id} c={c} />)}
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* EOIR Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GavelIcon sx={{ color: '#6A1B9A' }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="#6A1B9A">EOIR — Immigration Court</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {eoirCases.length} case{eoirCases.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </Box>
                <Button variant="outlined" size="small" startIcon={<AddIcon />}
                  onClick={() => { setNewCaseType('eoir'); setAddCaseOpen(true); if (selectedMember && selectedMember !== 'all') setNewCaseMemberId(String(selectedMember)); }}
                  sx={{ color: '#6A1B9A', borderColor: '#6A1B9A' }}>
                  Add court case
                </Button>
              </Box>
              {eoirCases.length === 0 ? (
                <Card sx={{ textAlign: 'center', p: 3, borderRadius: 2, border: '1px dashed #E1BEE7', background: '#FDF8FF' }}>
                  <Typography color="text.secondary" variant="body2">No court cases yet. Add an A-Number to track hearings and decisions.</Typography>
                </Card>
              ) : eoirCases.map(c => <EoirCard key={c.id} c={c} />)}
            </Box>

            {/* DOL Section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '1.3rem' }}>📄</Typography>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="#E65100">DOL — Labor Certification</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dolCases.length} case{dolCases.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </Box>
                <Button variant="outlined" size="small" startIcon={<AddIcon />}
                  onClick={() => { setNewCaseType('dol'); setAddCaseOpen(true); }}
                  sx={{ color: '#E65100', borderColor: '#E65100' }}>
                  Add DOL case
                </Button>
              </Box>
              {dolCases.length === 0 ? (
                <Card sx={{ textAlign: 'center', p: 3, borderRadius: 2, border: '1px dashed #FFCCBC', background: '#FFF8F5' }}>
                  <Typography color="text.secondary" variant="body2">No DOL cases yet. Add a case number from flag.dol.gov to track labor certifications.</Typography>
                </Card>
              ) : dolCases.map(c => (
                <Card key={c.id} sx={{ mb: 1.5, borderRadius: 2, border: '1px solid #FFE0B2' }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                          <Chip label="DOL" size="small" sx={{ background: '#FFF3E0', color: '#E65100', fontWeight: 700, fontSize: '0.7rem' }} />
                          {c.dol_case_type && <Chip label={c.dol_case_type} size="small" sx={{ fontSize: '0.7rem', background: '#F5F5F5' }} />}
                          <Typography fontWeight="bold" fontSize="0.9rem">{c.dol_case_number}</Typography>
                        </Box>
                        {c.dol_employer_name && (
                          <Typography variant="body2" sx={{ color: '#555', mb: 0.3 }}>
                            🏢 {c.dol_employer_name}
                          </Typography>
                        )}
                        {c.dol_job_title && (
                          <Typography variant="caption" sx={{ color: '#777', display: 'block', mb: 0.5 }}>
                            💼 {c.dol_job_title}
                          </Typography>
                        )}
                        {c.dol_case_status ? (
                          <Chip label={c.dol_case_status} size="small"
                            sx={{ mt: 0.3, fontSize: '0.7rem',
                              background: c.dol_case_status?.includes('CERTIFIED') && !c.dol_case_status?.includes('EXPIRED') ? '#E8F5E9' :
                                          c.dol_case_status?.includes('DENIED') ? '#FFEBEE' : '#FFF3E0',
                              color: c.dol_case_status?.includes('CERTIFIED') && !c.dol_case_status?.includes('EXPIRED') ? '#2E7D32' :
                                     c.dol_case_status?.includes('DENIED') ? '#C62828' : '#E65100' }} />
                        ) : (
                          <Typography variant="caption" color="text.secondary">⏳ Status not checked yet</Typography>
                        )}
                        {c.dol_submitted_date && (
                          <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                            Submitted: {new Date(c.dol_submitted_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </Typography>
                        )}
                      </Box>
                      <IconButton size="small" onClick={() => handleDelete(c.id)} sx={{ color: '#999' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </>
        )}
      </Container>

      {/* Dialog: Add Family Member */}
      <Dialog open={addMemberOpen} onClose={() => setAddMemberOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#2E7D32', fontWeight: 'bold' }}>👤 Add Family Member</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Full Name *" value={newMemberName}
            onChange={e => setNewMemberName(e.target.value)} sx={{ mt: 2, mb: 2 }}
            placeholder="e.g. Maria Panferova" />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Relationship</InputLabel>
            <Select value={newMemberRelation} label="Relationship" onChange={e => setNewMemberRelation(e.target.value)}>
              {RELATIONSHIP_TYPES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth label="A-Number (Alien Number)" value={newMemberAlien}
            onChange={e => setNewMemberAlien(e.target.value)} sx={{ mb: 2 }}
            placeholder="e.g. A-123-456-789" />
          <TextField fullWidth label="Date of Birth" value={newMemberDob}
            onChange={e => setNewMemberDob(e.target.value)} sx={{ mb: 2 }}
            placeholder="e.g. 01/15/1990" />
          <TextField fullWidth label="Notes (optional)" value={newMemberNotes}
            onChange={e => setNewMemberNotes(e.target.value)} multiline rows={2}
            placeholder="e.g. Pending I-589 asylum case" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMemberOpen(false)}>Cancel</Button>
          <Button onClick={handleAddMember} variant="contained" sx={{ background: '#2E7D32' }} disabled={!newMemberName.trim()}>Add Member</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Add Case (USCIS or EOIR) */}
      <Dialog open={addCaseOpen} onClose={() => { setAddCaseOpen(false); resetCaseForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: newCaseType === 'uscis' ? '#1565C0' : newCaseType === 'dol' ? '#E65100' : '#6A1B9A' }}>
          {newCaseType === 'uscis' ? '🏛️ Add USCIS Case' : newCaseType === 'dol' ? '📄 Add DOL Case' : '⚖️ Add Court Case (EOIR)'}
        </DialogTitle>
        <DialogContent>
          {/* Case type toggle */}
          <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
            <Button variant={newCaseType === 'uscis' ? 'contained' : 'outlined'}
              onClick={() => setNewCaseType('uscis')} size="small"
              sx={newCaseType === 'uscis' ? { background: '#1565C0' } : { color: '#1565C0', borderColor: '#1565C0' }}>
              USCIS Application
            </Button>
            <Button variant={newCaseType === 'eoir' ? 'contained' : 'outlined'}
              onClick={() => setNewCaseType('eoir')} size="small"
              sx={newCaseType === 'eoir' ? { background: '#6A1B9A' } : { color: '#6A1B9A', borderColor: '#6A1B9A' }}>
              Immigration Court
            </Button>
            <Button variant={newCaseType === 'dol' ? 'contained' : 'outlined'}
              onClick={() => setNewCaseType('dol')} size="small"
              sx={newCaseType === 'dol' ? { background: '#E65100' } : { color: '#E65100', borderColor: '#E65100' }}>
              DOL Labor
            </Button>
          </Box>
          {/* Family member dropdown */}
          {familyMembers.length > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Family Member</InputLabel>
              <Select value={newCaseMemberId} label="Family Member"
                onChange={e => { setNewCaseMemberId(e.target.value); if (newCaseType === 'eoir' && e.target.value) { const m = familyMembers.find(fm => fm.id === parseInt(e.target.value)); if (m?.alien_number) setNewAlienNumber(m.alien_number.replace(/\D/g, '')); } }}>
                <MenuItem value="">— No assignment —</MenuItem>
                {familyMembers.map(m => <MenuItem key={m.id} value={m.id}>{m.full_name} ({m.relationship_type})</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {/* USCIS fields */}
          {newCaseType === 'uscis' && (
            <TextField fullWidth label="Receipt Number (e.g. LIN9999106498)"
              value={newReceipt} onChange={e => setNewReceipt(e.target.value.toUpperCase())}
              sx={{ mb: 2 }} inputProps={{ maxLength: 13 }}
              helperText="3 letters + 10 digits, e.g. EAC9999103403" />
          )}
          {/* DOL fields */}
          {newCaseType === 'dol' && (
            <TextField fullWidth label="DOL Case Number"
              value={newDolNumber}
              onChange={e => setNewDolNumber(e.target.value.toUpperCase())}
              sx={{ mb: 1 }}
              placeholder="G-XXX-XXXXX-XXXXXX"
              error={newDolNumber.length > 5 && !/^[A-Z]-\d{3}-\d{5}-\d{6}$/.test(newDolNumber)}
              helperText={
                newDolNumber.length > 5 && !/^[A-Z]-\d{3}-\d{5}-\d{6}$/.test(newDolNumber)
                  ? '⚠️ Format must be: G-XXX-XXXXX-XXXXXX (e.g. letter, 3 digits, 5 digits, 6 digits)'
                  : 'Copy exactly from flag.dol.gov — format: G-XXX-XXXXX-XXXXXX'
              } />
          )}

          {/* EOIR fields */}
          {newCaseType === 'eoir' && (
            <>
              <TextField fullWidth label="A-Number *"
                value={newAlienNumber} onChange={e => setNewAlienNumber(e.target.value)}
                sx={{ mb: 2 }} placeholder="e.g. 246725959"
                helperText={newAlienNumber && newAlienNumber.replace(/\D/g,'').length === 9
                  ? '✓ A-Number looks correct'
                  : '9 digits — find it on your court documents (e.g. Notice to Appear)'}
                error={!!newAlienNumber && newAlienNumber.replace(/\D/g,'').length !== 9}
                InputProps={newAlienNumber && newAlienNumber.replace(/\D/g,'').length === 9
                  ? { sx: { '& fieldset': { borderColor: '#2e7d32 !important' } } } : {}} />
              <Autocomplete
                fullWidth
                sx={{ mb: 1 }}
                options={NATIONALITY_OPTIONS}
                getOptionLabel={o => o.label}
                value={NATIONALITY_OPTIONS.find(n => n.code === newNationalityCode) || null}
                onChange={(e, val) => setNewNationalityCode(val?.code || '')}
                renderInput={params => (
                  <TextField {...params} label="Nationality (as in EOIR system) *"
                    placeholder="Start typing to search..." />
                )}
              />
              <Alert severity="warning" icon={false} sx={{ mb: 2, fontSize: '0.75rem', py: 0.5 }}>
                ⚠️ Nationality must match <strong>exactly</strong> what EOIR has on file.
                Not sure? {' '}
                <a href="https://acis.eoir.justice.gov/en/" target="_blank" rel="noopener noreferrer"
                  style={{ color: '#E65100', fontWeight: 600 }}>
                  Verify at acis.eoir.justice.gov →
                </a>
              </Alert>
              <Alert severity="info" icon={false} sx={{ mb: 2, fontSize: '0.75rem', py: 0.5, background: '#E8F5E9', color: '#1B5E20', border: '1px solid #A5D6A7' }}>
                🔄 <strong>Automatic weekly check:</strong> Your case status will be checked every week and you'll receive an email if anything changes.
              </Alert>
            </>
          )}
          <TextField fullWidth label="Description (optional)" value={newDesc}
            onChange={e => setNewDesc(e.target.value)}
            placeholder={newCaseType === 'uscis' ? 'e.g. My I-485 Green Card application' : 'e.g. Denis removal case — asylum granted'} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddCaseOpen(false); resetCaseForm(); }}>Cancel</Button>
          <Button onClick={handleAddCase} variant="contained"
            disabled={newCaseType === 'uscis' ? !canAddUscis : newCaseType === 'dol' ? !canAddDol : !canAddEoir}
            sx={{ background: newCaseType === 'uscis' ? '#1565C0' : '#6A1B9A' }}>
            Add Case
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Status History */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
          Status History —{' '}
          {historyData?.case?.receipt_number || `A-${historyData?.case?.eoir_alien_number}`}
          {historyData?.case?.form_type && ` (${historyData.case.form_type})`}
        </DialogTitle>
        <DialogContent>
          {historyData?.total_checks === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              No checks yet. Status will be updated automatically each week.
            </Typography>
          ) : historyData?.case?.case_type === 'eoir' ? (
            // EOIR history
            historyData?.eoir_checks?.map((check, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                {/* Timeline dot */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: check.status_changed ? '#2E7D32' : '#E0E0E0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1rem', flexShrink: 0
                  }}>
                    {check.status_changed ? '🔔' : '✓'}
                  </Box>
                  {i < historyData.eoir_checks.length - 1 && (
                    <Box sx={{ width: 2, flex: 1, background: '#E0E0E0', mt: 0.5 }} />
                  )}
                </Box>
                {/* Content */}
                <Box sx={{ flex: 1, pb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography fontWeight="bold" color={check.status_changed ? '#2E7D32' : 'text.primary'} sx={{ fontSize: '0.9rem' }}>
                      {check.status_changed ? (check.status_summary || 'Status Changed') : 'No changes detected'}
                    </Typography>
                    {check.status_changed && <Chip label="Changed!" color="success" size="small" sx={{ ml: 1 }} />}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
                    {new Date(check.checked_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                  {check.decision && (
                    <Typography variant="body2" sx={{ mt: 0.5, color: '#2E7D32', fontStyle: 'italic' }}>
                      ⚖️ {check.decision}
                    </Typography>
                  )}
                  {check.hearing_date && (
                    <Typography variant="body2" sx={{ mt: 0.5, color: '#1565C0' }}>
                      📅 Next hearing: {fmtDate(check.hearing_date)}
                    </Typography>
                  )}
                  {check.pending_at_bia && (
                    <Typography variant="body2" sx={{ mt: 0.5, color: '#E65100' }}>
                      ⚖️ Pending at BIA
                    </Typography>
                  )}
                </Box>
              </Box>
            ))
          ) : (
            // USCIS history
            historyData?.checks?.map((check, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                {/* Timeline dot */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: check.status_changed ? '#1565C0' : '#E0E0E0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1rem', flexShrink: 0
                  }}>
                    {check.status_changed ? '🔔' : '✓'}
                  </Box>
                  {i < historyData.checks.length - 1 && (
                    <Box sx={{ width: 2, flex: 1, background: '#E0E0E0', mt: 0.5 }} />
                  )}
                </Box>
                {/* Content */}
                <Box sx={{ flex: 1, pb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography fontWeight="bold" color={check.status_changed ? '#1565C0' : 'text.primary'} sx={{ fontSize: '0.9rem' }}>
                      {check.status || 'Status checked'}
                    </Typography>
                    {check.status_changed && <Chip label="Changed!" color="primary" size="small" sx={{ ml: 1 }} />}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
                    {new Date(check.checked_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    {check.status_date && ` • Status date: ${fmtDate(check.status_date)}`}
                  </Typography>
                  {check.details && (
                    <Typography variant="body2" sx={{ mt: 0.5, color: '#555', whiteSpace: 'pre-line' }}>{check.details}</Typography>
                  )}
                </Box>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)} sx={{ color: '#2E7D32' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Account Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => { setSettingsOpen(false); setSettingsMsg(''); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>⚙️ Account Settings</DialogTitle>
        <DialogContent>
          {settingsMsg && (
            <Alert severity={settingsMsg.startsWith('✅') ? 'success' : 'error'} sx={{ mb: 2 }}>
              {settingsMsg}
            </Alert>
          )}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>PROFILE</Typography>
          <TextField fullWidth label="Full Name" value={settingsName}
            onChange={e => setSettingsName(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth label="Email" value={settingsEmail}
            disabled sx={{ mb: 2 }} helperText="Contact support to change email" />

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>NOTIFICATIONS</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            p: 1.5, border: '1px solid #E0E0E0', borderRadius: 1, mb: 1 }}>
            <Box>
              <Typography variant="body2" fontWeight="bold">📬 Email notifications</Typography>
              <Typography variant="caption" color="text.secondary">
                Receive email updates when your case status changes
              </Typography>
            </Box>
            <Box sx={{ width: 48, height: 24, borderRadius: 12, cursor: 'pointer',
              background: settingsEmailNotif ? '#2E7D32' : '#ccc',
              display: 'flex', alignItems: 'center',
              justifyContent: settingsEmailNotif ? 'flex-end' : 'flex-start', p: '2px' }}
              onClick={() => setSettingsEmailNotif(!settingsEmailNotif)}>
              <Box sx={{ width: 20, height: 20, borderRadius: '50%', background: 'white' }} />
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Member since: {new Date(user?.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSettingsOpen(false); setSettingsMsg(''); }}>Cancel</Button>
          <Button onClick={handleSaveSettings} variant="contained"
            disabled={settingsLoading} sx={{ background: '#2E7D32' }}>
            {settingsLoading ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={deleteOpen} onClose={() => { setDeleteOpen(false); setDeleteConfirm(''); setDeleteReason(''); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#C62828', fontWeight: 'bold' }}>
          ⚠️ Delete Account
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will permanently delete your account, all cases, family members and history. This action cannot be undone.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Why are you leaving? (optional)
          </Typography>
          {['No longer need the service', 'Found a better solution', 'Privacy concerns', 'Too expensive', 'Other'].map(reason => (
            <Box key={reason} sx={{ display: 'flex', alignItems: 'center', mb: 0.5, cursor: 'pointer' }}
              onClick={() => setDeleteReason(reason)}>
              <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #C62828',
                background: deleteReason === reason ? '#C62828' : 'transparent', mr: 1, flexShrink: 0 }} />
              <Typography variant="body2">{reason}</Typography>
            </Box>
          ))}
          <TextField fullWidth sx={{ mt: 2 }}
            label='Type "DELETE" to confirm'
            value={deleteConfirm}
            onChange={e => setDeleteConfirm(e.target.value)}
            error={deleteConfirm.length > 0 && deleteConfirm !== 'DELETE'}
            helperText={deleteConfirm.length > 0 && deleteConfirm !== 'DELETE' ? 'Type DELETE in capitals' : ''} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteOpen(false); setDeleteConfirm(''); setDeleteReason(''); }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteAccount}
            disabled={deleteConfirm !== 'DELETE' || deleteLoading}
            variant="contained" color="error">
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete My Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Footer */}
      <Box sx={{ textAlign: 'center', py: 2, mt: 2, borderTop: '1px solid #E0E0E0' }}>
        <Typography variant="caption" color="text.secondary">
          © 2026 Panferov LLC{' '}|{' '}
          <a href="/terms" target="_blank" style={{ color: '#2E7D32' }}>Terms of Service</a>
          {' '}|{' '}
          <a href="/privacy" target="_blank" style={{ color: '#2E7D32' }}>Privacy Policy</a>
        </Typography>
      </Box>
    </Box>
  );
}